import { morphSection } from '@theme/section-renderer';

/**
 * Cart-side tier discount decider.
 *
 * Runs as a CUSTOM ELEMENT, not on DOMContentLoaded. The theme re-renders
 * the cart through the Section Rendering API, which injects markup with
 * innerHTML — and a <script> tag inserted that way NEVER EXECUTES. Custom
 * elements are upgraded whenever they appear, including via innerHTML. The
 * script itself is loaded unconditionally from cart-drawer.liquid and
 * main-cart.liquid, never from inside a cart.empty? branch.
 *
 * Both inputs come from Liquid (see bundle-units.liquid), not from
 * /cart.js, whose discount_codes field is not guaranteed to exist.
 *
 *   3 or more units -> BUNDLE25 (25%)
 *   exactly 2 units -> BUNDLE15 (15%)
 *   fewer           -> no tier code
 *
 * WHAT CHANGED (cart v2)
 * ----------------------
 * 1. Unit counting in bundle-units.liquid now EXCLUDES free-gift lines, so
 *    an auto-added gift can no longer promote the cart to a tier the paid
 *    items have not earned.
 * 2. Success no longer reloads the page. The write requests the cart
 *    section in the same response and morphs it in — same pattern as the
 *    theme's own discount field (cart-discount.js).
 * 3. Writes go through window.__cartOpChain, shared with cart-rewards.js,
 *    so the tier decider and the gift engine can never interleave.
 * 4. attributeChangedCallback re-runs the decider when a morph patches the
 *    element in place (connectedCallback alone only covers full swaps).
 *
 * ATTEMPT GUARD (unchanged)
 * -------------------------
 * Shopify may decline a code the theme offers (collection scoping,
 * minimums). A declined code never appears in cart.discount_applications,
 * so without a guard the next render would retry forever. The guard
 * records the cart signature already attempted; changing the cart changes
 * the signature and allows a fresh attempt.
 */

const TIERS = [
  { min: 3, code: 'BUNDLE25' },
  { min: 2, code: 'BUNDLE15' },
];

const ALL_TIER_CODES = TIERS.map((tier) => tier.code);
const ATTEMPT_KEY = 'bundle-attempt';

function root() {
  return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
}

/** Same global chain as cart-rewards.js — one cart writer at a time. */
function enqueueCartOp(op) {
  const previous = window.__cartOpChain || Promise.resolve();
  const next = previous.catch(() => {}).then(op);
  window.__cartOpChain = next.catch(() => {});
  return next;
}

function wantedCode(units) {
  const tier = TIERS.find((entry) => units >= entry.min);
  return tier ? tier.code : null;
}

function readAttempt() {
  try {
    return sessionStorage.getItem(ATTEMPT_KEY);
  } catch {
    return null;
  }
}

function writeAttempt(signature) {
  try {
    sessionStorage.setItem(ATTEMPT_KEY, signature);
  } catch {
    /* Private mode: the guard is best-effort. Worst case is a retry. */
  }
}

async function apply() {
  const el = document.querySelector('bundle-discount');
  if (!el) return;

  const units = Number(el.dataset.units || 0);
  const appliedRaw = el.dataset.applied || '';
  const sectionId = el.dataset.sectionId || 'cart-drawer-section';
  const inDrawer = Boolean(el.closest('theme-drawer'));

  const applied = appliedRaw
    .split(',')
    .map((code) => code.trim())
    .filter(Boolean);

  const target = wantedCode(units);
  const currentTier = applied.find((code) => ALL_TIER_CODES.includes(code)) || null;

  console.info(
    `[bundle] units=${units} applied=[${applied.join(', ') || 'none'}] have=${currentTier || 'none'} want=${
      target || 'none'
    }`
  );

  if (currentTier === target) {
    writeAttempt('');
    return;
  }

  const signature = `${units}:${target || 'none'}`;
  if (readAttempt() === signature) {
    console.info('[bundle] This cart state was already attempted and declined; not retrying');
    return;
  }
  writeAttempt(signature);

  // Keep anything the customer typed; replace only our tier codes.
  const kept = applied.filter((code) => !ALL_TIER_CODES.includes(code));
  const next = target ? [...kept, target] : kept;

  const response = await fetch(`${root()}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ discount: next.join(','), sections: [sectionId] }),
  });

  if (!response.ok) throw new Error(`cart/update.js responded ${response.status}`);

  const data = await response.json();
  const html = data.sections && data.sections[sectionId];
  if (html) morphSection(sectionId, html, { mode: inDrawer ? 'hydration' : 'full' });
}

class BundleDiscount extends HTMLElement {
  static observedAttributes = ['data-units', 'data-applied'];

  connectedCallback() {
    this.#schedule();
  }

  attributeChangedCallback(_name, oldValue, newValue) {
    if (oldValue !== null && oldValue !== newValue) this.#schedule();
  }

  #schedule() {
    clearTimeout(this._applyTimer);
    this._applyTimer = setTimeout(
      () => enqueueCartOp(() => apply().catch((error) => console.warn('[bundle] Write failed:', error))),
      150
    );
  }
}

if (!customElements.get('bundle-discount')) {
  customElements.define('bundle-discount', BundleDiscount);
}
